import React, { MouseEvent, TouchEvent, useCallback, useEffect, useRef, useState } from "react";

import { Column, ColumnEvent, IHeaderParams } from "@ag-grid-community/core";
import { faCircle } from "@fortawesome/pro-solid-svg-icons/faCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";

import styles from "./BaseHeader.module.scss";

const getColumnSortDirection = (column: Column): SortOrder | undefined =>
  column.isSortAscending() ? SortOrder.ASC : column.isSortDescending() ? SortOrder.DESC : undefined;

export interface BaseHeaderProps extends Omit<IHeaderParams, "displayName"> {
  displayName?: React.ReactNode;
  headingRender?: (props: Omit<IHeaderParams, "displayName">) => React.ReactNode;
  trailingRender?: (props: Omit<IHeaderParams, "displayName">) => React.ReactNode;
}

/**
 * Custom Header implementation which mimics native AgGrid's header.
 * Can be used as a base for other custom headers.
 */
const BaseHeader: React.FC<BaseHeaderProps> = ({
  headingRender,
  trailingRender,
  ...otherProps
}) => {
  const {
    column,
    columnApi,
    displayName,
    enableMenu,
    enableSorting,
    progressSort,
    showColumnMenu,
  } = otherProps;
  // a somewhat dirty hack to set width of the ag-react-container to 100%
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.parentElement!.style.width = "100%";
    }
  }, []);

  const [sortDirection, setSortDirection] = useState<SortOrder | undefined>(() =>
    getColumnSortDirection(column)
  );
  const handleSortChanged = useCallback(
    (event: ColumnEvent) => {
      if (event.column) {
        setSortDirection(getColumnSortDirection(event.column));
      }
    },
    [setSortDirection]
  );
  useEffect(() => {
    column.addEventListener("sortChanged", handleSortChanged);
    return () => column.removeEventListener("sortChanged", handleSortChanged);
  }, [column, handleSortChanged]);

  const [filterActive, setFilterActive] = useState<boolean>(column.isFilterActive());
  const handleFilterChanged = useCallback(() => {
    setFilterActive(column.isFilterActive());
  }, [column, setFilterActive]);
  useEffect(() => {
    column.addEventListener("filterChanged", handleFilterChanged);
    return () => column.removeEventListener("filterChanged", handleFilterChanged);
  }, [column, handleFilterChanged]);

  const handleHeaderClick = useCallback(
    (event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
      if (enableSorting) {
        // It actually should be either event.shiftKey or event.ctrlKey depending on the
        // multiSortKey grid prop. But there's no getter for this prop, so using a default
        // shiftKey.
        progressSort(event.shiftKey);
      }
    },
    [enableSorting, progressSort]
  );

  const menuButtonRef = useRef<HTMLSpanElement>(null);
  const handleMenuClick = useCallback(() => {
    if (enableMenu && menuButtonRef.current) {
      showColumnMenu(menuButtonRef.current);
    }
  }, [enableMenu, showColumnMenu]);

  const isSortOrderHidden = columnApi
    .getColumnState()
    .every((state) => !state.sortIndex || state.sortIndex < 2);
  const isNoSortHidden = !!sortDirection || !column.getColDef().unSortIcon;

  return (
    <div className={styles.container} ref={containerRef}>
      {headingRender && headingRender(otherProps)}
      <div className={styles.labelContainer}>
        <div
          className="ag-header-cell-label"
          onClick={handleHeaderClick}
          onTouchEnd={handleHeaderClick}
          role="presentation"
          unselectable="on"
        >
          <span className="ag-header-cell-text" unselectable="on">
            {displayName}
          </span>
          <span
            aria-hidden="true"
            className={cn("ag-header-icon ag-header-label-icon ag-filter-icon", {
              "ag-hidden": !filterActive,
            })}
          >
            <FontAwesomeIcon className={styles.filterIcon} icon={faCircle} />
          </span>
          <span
            aria-hidden="true"
            className={cn("ag-header-icon ag-header-label-icon ag-sort-order", {
              "ag-hidden": isSortOrderHidden,
            })}
          >
            {column.getSortIndex()}
          </span>
          <span
            aria-hidden="true"
            className={cn("ag-header-icon ag-header-label-icon ag-sort-ascending-icon", {
              "ag-hidden": sortDirection !== SortOrder.ASC,
            })}
          >
            <span className="ag-icon ag-icon-asc" role="presentation" unselectable="on" />
          </span>
          <span
            aria-hidden="true"
            className={cn("ag-header-icon ag-header-label-icon ag-sort-descending-icon", {
              "ag-hidden": sortDirection !== SortOrder.DESC,
            })}
          >
            <span className="ag-icon ag-icon-desc" role="presentation" unselectable="on" />
          </span>
          <span
            aria-hidden="true"
            className={cn("ag-header-icon ag-header-label-icon ag-sort-none-icon", {
              "ag-hidden": isNoSortHidden,
            })}
          >
            <span className="ag-icon ag-icon-none" role="presentation" unselectable="on" />
          </span>
        </div>
        {enableMenu && (
          <span
            aria-hidden="true"
            className="ag-header-icon ag-header-cell-menu-button"
            onClick={handleMenuClick}
            onTouchEnd={handleMenuClick}
            ref={menuButtonRef}
          >
            <span className="ag-icon ag-icon-menu" role="presentation" unselectable="on" />
          </span>
        )}
      </div>
      {trailingRender && trailingRender(otherProps)}
    </div>
  );
};

export default BaseHeader;
